import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { get, post } from "../../api/api";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Toolbar
} from "@material-ui/core";
import { Check } from "@mui/icons-material";
import { MobilePOSActivationType, Tenant } from "../../models/Tenant";
import MobilePOSStoreList from "./MobilePOSStoreList";
import useStyles from "./styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";
import { Authenticated, hasRole } from "../../models/User";
import { UserContext } from "../../UserProvider";
import { useI18n } from "../../hooks/useI18n";

const fetchTenantChains = async () => {
  const response = await get("/v1/mpos/tenant-chains");

  const data = await response.json();
  return data;
};

const mapActivationType = (type: string): number | undefined => {
  const mapping: Record<string, number> = {
    "": MobilePOSActivationType.NONE,
    KRIS: MobilePOSActivationType.KRIS,
    KRIS_HIGH: MobilePOSActivationType.KRIS_HIGH
  };

  return mapping[type];
};

export const activateMPOS = async (payload: any) => {
  const activationType = mapActivationType(payload.activationType);
  debugger;
  const response = await post(
    `/v1/mpos/toggle-chain-mobile-pos-activation?activate=${payload.activate}&tenantChainId=${payload.id}&activationType=${activationType}`
  );
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail);
  }

  const data = await response.json();
  return data;
};

const ChainMobilePOSControl: React.FC = ({}) => {
  const classes = useStyles();
  const { t } = useI18n();

  const user = useContext(UserContext) as Authenticated;

  const { data, isLoading, isError } = useQuery(
    ["tenantChains"],
    () => fetchTenantChains(),
    {
      staleTime: Infinity
    }
  );

  const [showStartMobilePOS, setShowStartMobilePOS] = useState(false);
  const [selectedChain, setSelectedChain] = useState({
    id: "",
    name: "",
    activate: false
  });

  const [selectedOption, setSelectedOption] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleStartClick = () => {
    setConfirmDialogOpen(true);
  };

  const [activateError, setActivateError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutate, isLoading: approveRequestLoading } = useMutation(
    activateMPOS,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tenantChains"]);
        queryClient.invalidateQueries(["tenantChainStores", selectedChain.id]);
        setSelectedChain({
          id: "",
          name: "",
          activate: false
        });
        setConfirmDialogOpen(false);
        setShowStartMobilePOS(false);
      },
      onError: (err: Error | any) => {
        console.error("POST activate request failed:", err);
        setActivateError(err.message || t("common.messages.failedToActivate"));
      }
    }
  );

  return (
    <div>
      <MaterialTable
        isLoading={isLoading}
        style={{ boxShadow: "none" }}
        title=""
        columns={[
          {
            title: t("common.formFields.tenantChain"),
            field: "name"
          },
          {
            title: t("common.formFields.activationType"),
            field: "mobilePOSActivationType",
            render: (rowData: Tenant) => {
              if (rowData.mobilePOSActive === true) {
                return rowData.mobilePOSActivationType;
              }
              return "";
            }
          },
          {
            render: (rowData: any) => (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {rowData.mobilePOSActive === true ? (
                  <Button
                    startIcon={<Check />}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedChain({
                        id: rowData.id,
                        name: rowData.name,
                        activate: false
                      });
                      setShowStartMobilePOS(true);
                    }}
                  >
                    {t("common.actions.stop")}
                  </Button>
                ) : (
                  <Button
                    startIcon={<Check />}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedChain({
                        id: rowData.id,
                        name: rowData.name,
                        activate: true
                      });
                      setShowStartMobilePOS(true);
                    }}
                  >
                    {t("common.actions.start")}
                  </Button>
                )}
              </div>
            ),
            cellStyle: { textAlign: "right" },
            headerStyle: { textAlign: "right" }
          }
        ]}
        detailPanel={(rowData: any) => (
          <MobilePOSStoreList tenantChainId={rowData.id} />
        )}
        data={data}
        options={{
          search: false,
          paging: false
        }}
        components={{
          Container: props => <Paper {...props} elevation={0} />,
          Toolbar: props => (
            <div style={{ height: "1px" }} className={classes.toolbar}>
              <MTableToolbar {...props} />
            </div>
          )
        }}
      />

      <Dialog
        fullWidth
        maxWidth={"sm"}
        open={showStartMobilePOS}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {`${
            selectedChain.activate
              ? t("common.messages.mobilePosStartIn")
              : t("common.messages.mobilePosStopIn")
          } ${selectedChain.name}`}
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => {
              setSelectedChain({ id: "", name: "", activate: false });
              setShowStartMobilePOS(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          {!selectedChain.activate ? (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  startIcon={<Check />}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleStartClick}
                >
                  {t("common.actions.stop")}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="mobile-pos"
                    name="mobile-pos"
                    value={selectedOption}
                    onChange={e => setSelectedOption(e.target.value)}
                  >
                    <FormControlLabel
                      value="KRIS"
                      control={<Radio />}
                      label={t("common.messages.kris")}
                    />
                    {hasRole(user, ["ITAdmin"]) && (
                      <FormControlLabel
                        value={"KRIS_HIGH"}
                        control={<Radio />}
                        label={t("common.messages.krisHigh")}
                      />
                    )}
                  </RadioGroup>
                </FormControl>

                <Button
                  startIcon={<Check />}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleStartClick}
                  disabled={!selectedOption}
                >
                  {t("common.actions.start")}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogContent>
          {selectedChain.activate
            ? t("common.messages.mobilePosStartConfirmation")
            : t("common.messages.mobilePosStopConfirmation")}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false), setActivateError("");
            }}
            color="secondary"
          >
            {t("common.actions.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (
                !selectedChain.id ||
                (selectedChain.activate && !selectedOption)
              ) {
                setActivateError(
                  t("common.messages.pleaseSelectActivationType")
                );
                return;
              }

              mutate({
                id: selectedChain.id,
                activationType: selectedOption,
                activate: selectedChain.activate
              });
            }}
            color="primary"
            variant="contained"
            disabled={approveRequestLoading}
          >
            {t("common.actions.confirm")}
          </Button>
        </DialogActions>
        {activateError && (
          <Box sx={{ marginTop: 2, padding: 2 }}>
            <Alert severity="error">{activateError}</Alert>
          </Box>
        )}
      </Dialog>
    </div>
  );
};

export default ChainMobilePOSControl;
